export type Relationship = 'spouse' | 'child' | 'grandchild' | 'father' | 'mother';

export type FamilyStep =
  | 'intro'
  | 'hasMembers'
  | 'relationship'
  | 'fullName'
  | 'fullNameKana'
  | 'dob'
  | 'sameHousehold'
  | 'ifNotAddress'
  | 'outsideJapan'
  | 'insuranceGate'
  | 'insuranceRequired'
  | 'myNumberLink'
  | 'healthDependent'
  | 'supportedDependent'
  | 'myNumber'
  | 'otherIncomeGate'
  | 'otherEarnerName'
  | 'otherEarnerAddress'
  | 'taxGate'
  | 'sharedLivelihood'
  | 'spouseFamilyWorker'
  | 'estimatedIncome'
  | 'dependentIneligibleNotice'
  | 'taxDependentCategory'
  | 'taxNonResident'
  | 'disabilityStatus'
  | 'disabilityFacts'
  | 'changeDuringYear'
  | 'changeDate'
  | 'changeReason'
  | 'changeReasonOther'
  | 'nonResidentReason'
  | 'nonResidentReasonOther'
  | 'remittanceAmount'
  | 'addAnother'
  | 'finalConfirm';

export type YesNo = 'yes' | 'no';

export type MyNumberLink = 'linked' | 'not_linked';

export type DisabilityCategory = 'none' | 'general' | 'special';

export type ThreadMessage = {
  role: 'assistant' | 'user';
  text: string;
};

/** Collapsed member summary + snapshot to re-expand Q&A on undo */
export type CompletedMemberEntry = {
  text: string;
  snapshot: FamilyMemberDraft;
};

export type FamilyMemberDraft = {
  id?: number;
  relationship: Relationship | '';
  fullName: string;
  fullNameKana: string;
  dateOfBirth: string;
  /** Raw 12-digit My Number; only collected when tax/insurance gates require it. */
  myNumber: string;
  livingInSameHousehold: YesNo | '';
  ifNotAddress: string;
  /** Yes = lives outside Japan (only asked when not same household). */
  outsideJapan: YesNo | '';
  nonResidentFlag: boolean;
  registerHealthInsurance: YesNo | '';
  insuranceRequired: YesNo | '';
  myNumberLinkStatus: MyNumberLink | '';
  healthInsuranceDependent: YesNo | '';
  supportedDependent: YesNo | '';
  claimedByOtherIncome: YesNo | '';
  otherEarnerName: string;
  otherEarnerAddress: string;
  claimTaxDependent: YesNo | '';
  taxYear: number;
  /** Spouse Section 2 — shared livelihood (生計を一にする). */
  sharedLivelihood: YesNo | '';
  /** Spouse Section 2 — blue/white return family worker (専従者). */
  spouseFamilyWorker: YesNo | '';
  disabilityCategory: DisabilityCategory | '';
  /** Free-text disability particulars (stored as disabilityFactsJson). */
  disabilityFacts: string;
  changeDuringYear: YesNo | '';
  estimatedIncome: string;
  withholdingSpouse: YesNo | '';
  taxEligible: YesNo | '';
  /** One kanji label stored in tax_dependent_category (non-spouse). */
  taxDependentCategory: string;
  /** Tax non-resident (国外居住) — asked for all non-spouse tax dependents. */
  taxNonResident: YesNo | '';
  taxIneligibilityReason: string;
  /** Coded reason or free text when "none of the above". */
  nonResidentReason: string;
  remittanceAmount: string;
  taxChangeReason: string;
  taxChangeDate: string;
};

export function emptyMemberDraft(taxYear: number): FamilyMemberDraft {
  return {
    relationship: '',
    fullName: '',
    fullNameKana: '',
    dateOfBirth: '',
    myNumber: '',
    livingInSameHousehold: '',
    ifNotAddress: '',
    outsideJapan: '',
    nonResidentFlag: false,
    registerHealthInsurance: '',
    insuranceRequired: '',
    myNumberLinkStatus: '',
    healthInsuranceDependent: '',
    supportedDependent: '',
    claimedByOtherIncome: '',
    otherEarnerName: '',
    otherEarnerAddress: '',
    claimTaxDependent: '',
    taxYear,
    sharedLivelihood: '',
    spouseFamilyWorker: '',
    disabilityCategory: '',
    disabilityFacts: '',
    changeDuringYear: '',
    estimatedIncome: '',
    withholdingSpouse: '',
    taxEligible: '',
    taxDependentCategory: '',
    taxNonResident: '',
    taxIneligibilityReason: '',
    nonResidentReason: '',
    remittanceAmount: '',
    taxChangeReason: '',
    taxChangeDate: '',
  };
}

export type FamilyMemberApi = {
  id?: number;
  relationship?: string;
  fullName?: string;
  fullNameKana?: string;
  dateOfBirth?: string;
  myNumber?: string;
  /** none | general | special */
  disabilityCategory?: string;
  /** Disability particulars JSON: {"description":"..."} */
  disabilityFactsJson?: string;
  livingInSameHousehold?: boolean;
  ifNotAddress?: string;
  nonResidentFlag?: boolean;
  registerHealthInsurance?: boolean;
  insuranceRequired?: boolean;
  myNumberLinkStatus?: string;
  healthInsuranceDependent?: boolean;
  supportedDependent?: boolean;
  claimedByOtherIncome?: boolean;
  otherEarnerName?: string;
  otherEarnerAddress?: string;
  claimTaxDependent?: boolean;
  taxYear?: number;
  sharedLivelihood?: boolean;
  familyWorker?: boolean;
  changeDuringYear?: boolean;
  estimatedIncome?: number | string;
  withholdingSpouse?: boolean;
  taxEligible?: boolean;
  nonResidentReason?: string;
  remittanceAmount?: number | string;
  taxChangeReason?: string;
  taxChangeDate?: string;
  taxCategories?: string[];
};
